import { useState, useCallback } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Cell,
} from "recharts";
import { COMPOUND_DATABASE } from "@/data/compoundDatabase";
import {
  useCompoundIdentification,
  useHazardAssessment,
} from "@/hooks/useAIModels";
import AIEngineBadge from "@/components/AIEngineBadge";
import type {
  DeargenCompoundIdentificationResponse,
  DeargenHazardAssessmentResponse,
} from "@/types";
import { riskBadgeClass } from "@/utils/riskBadge";

/** 샘플 피크 데이터 (벤젠, 톨루엔, 에탄올) */
const SAMPLE_PEAKS = {
  benzene: {
    label: "벤젠",
    smiles: "c1ccccc1",
    peaks: [
      { mz: 78, intensity: 100 },
      { mz: 77, intensity: 25 },
      { mz: 51, intensity: 20 },
      { mz: 50, intensity: 15 },
      { mz: 52, intensity: 12 },
    ],
  },
  toluene: {
    label: "톨루엔",
    smiles: "Cc1ccccc1",
    peaks: [
      { mz: 91, intensity: 100 },
      { mz: 92, intensity: 60 },
      { mz: 65, intensity: 18 },
      { mz: 39, intensity: 12 },
    ],
  },
  ethanol: {
    label: "에탄올",
    smiles: "CCO",
    peaks: [
      { mz: 31, intensity: 100 },
      { mz: 45, intensity: 50 },
      { mz: 29, intensity: 30 },
      { mz: 27, intensity: 20 },
    ],
  },
};

type SampleKey = keyof typeof SAMPLE_PEAKS;

function jsdColor(jsd: number): string {
  if (jsd < 0.2) return "#22c55e";
  if (jsd < 0.4) return "#84cc16";
  if (jsd < 0.6) return "#eab308";
  return "#ef4444";
}

export default function CompoundTester() {
  const [smiles, setSmiles] = useState("");
  const [peaksText, setPeaksText] = useState("");
  const [identResult, setIdentResult] =
    useState<DeargenCompoundIdentificationResponse | null>(null);
  const [hazardResult, setHazardResult] =
    useState<DeargenHazardAssessmentResponse | null>(null);

  const identMutation = useCompoundIdentification();
  const hazardMutation = useHazardAssessment();

  const loadSample = useCallback((key: SampleKey) => {
    const sample = SAMPLE_PEAKS[key];
    setSmiles(sample.smiles);
    setPeaksText(JSON.stringify(sample.peaks, null, 2));
  }, []);

  const handleRun = useCallback(() => {
    let peaks: { mz: number; intensity: number }[];
    try {
      peaks = JSON.parse(peaksText) as { mz: number; intensity: number }[];
    } catch {
      alert("피크 데이터 JSON 형식이 올바르지 않습니다.");
      return;
    }

    // Compound identification (sample_id=1: 개발자 테스트용 더미 값)
    identMutation.mutate(
      {
        sample_id: 1,
        spectrum_type: "gcms",
        peaks,
      },
      {
        onSuccess: (data) => {
          setIdentResult(data);
          // Run hazard assessment with identified compounds
          if (data.identified_compounds.length > 0) {
            hazardMutation.mutate(
              {
                compounds: data.identified_compounds.map((c) => ({
                  compound_name: c.compound_name,
                  cas_number: c.cas_number,
                  smiles: c.structure?.smiles ?? smiles,
                })),
              },
              { onSuccess: setHazardResult },
            );
          }
        },
      },
    );
  }, [peaksText, smiles, identMutation, hazardMutation]);

  const isLoading = identMutation.isPending || hazardMutation.isPending;

  // Prepare chart data from identification results
  const chartData = identResult
    ? identResult.identified_compounds.slice(0, 5).map((c) => ({
        name: c.compound_name_ko ?? c.compound_name,
        score: Math.round(c.match_score * 100),
        jsd:
          "jsd_distance" in c
            ? (c as Record<string, unknown>).jsd_distance
            : null,
      }))
    : [];

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <div className="w-2 h-2 bg-blue-500 rounded-full" />
        화합물 테스트
      </h3>

      <div className="grid grid-cols-12 gap-6">
        {/* Left: Input */}
        <div className="col-span-12 lg:col-span-5 space-y-4">
          {/* SMILES Input */}
          <div>
            <label
              htmlFor="smiles-input"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              SMILES
            </label>
            <input
              id="smiles-input"
              type="text"
              value={smiles}
              onChange={(e) => setSmiles(e.target.value)}
              placeholder="예: c1ccccc1 (벤젠)"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none font-mono"
            />
          </div>

          {/* Quick Select */}
          <div>
            <label
              htmlFor="quick-select"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              퀵 셀렉트
            </label>
            <select
              id="quick-select"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              onChange={(e) => {
                const compound = COMPOUND_DATABASE.find(
                  (c) => c.smiles === e.target.value,
                );
                if (compound) {
                  setSmiles(compound.smiles);
                }
              }}
              value=""
            >
              <option value="">화합물 선택...</option>
              {COMPOUND_DATABASE.map((c) => (
                <option key={c.cas_number} value={c.smiles}>
                  {c.compound_name_ko} ({c.compound_name})
                </option>
              ))}
            </select>
          </div>

          {/* Peaks Input */}
          <div>
            <label
              htmlFor="peaks-input"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              m/z 피크 데이터 (JSON)
            </label>
            <textarea
              id="peaks-input"
              value={peaksText}
              onChange={(e) => setPeaksText(e.target.value)}
              placeholder='[{"mz":91,"intensity":100},{"mz":92,"intensity":60}]'
              rows={5}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none font-mono"
            />
          </div>

          {/* Sample Data Buttons */}
          <div className="flex gap-2 flex-wrap">
            {(Object.entries(SAMPLE_PEAKS) as [SampleKey, (typeof SAMPLE_PEAKS)[SampleKey]][]).map(
              ([key, sample]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => loadSample(key)}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                >
                  {sample.label} 예시
                </button>
              ),
            )}
          </div>

          {/* Run Button */}
          <button
            type="button"
            onClick={handleRun}
            disabled={!peaksText || isLoading}
            className="w-full py-2.5 px-4 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                분석 중...
              </>
            ) : (
              "분석 실행"
            )}
          </button>

          {identMutation.isError && (
            <p className="text-sm text-red-600">
              AI 서비스 연결에 실패했습니다. 서비스가 실행 중인지 확인하세요.
            </p>
          )}
        </div>

        {/* Right: Results */}
        <div className="col-span-12 lg:col-span-7">
          {!identResult && !isLoading && (
            <div className="flex items-center justify-center h-full min-h-[300px] bg-gray-50 rounded-lg border border-dashed border-gray-300">
              <p className="text-sm text-gray-500 text-center px-4">
                화합물을 선택하거나 SMILES를 입력하여
                <br />
                AI 모델을 테스트하세요
              </p>
            </div>
          )}

          {identResult && (
            <div className="space-y-4">
              {/* Engine Badge */}
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700">
                  동정 엔진:
                </span>
                <AIEngineBadge
                  engine={
                    "identification_engine" in identResult
                      ? (identResult as Record<string, unknown>)
                          .identification_engine as string
                      : null
                  }
                />
                <span className="text-xs text-gray-500">
                  {identResult.total_matches_found}건 매칭
                </span>
              </div>

              {/* JSD Match Chart */}
              {chartData.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                    상위 매칭 결과
                  </h4>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={chartData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        type="number"
                        domain={[0, 100]}
                        tick={{ fontSize: 11 }}
                        label={{
                          value: "매칭 점수 (%)",
                          position: "insideBottom",
                          offset: -5,
                          fontSize: 11,
                        }}
                      />
                      <YAxis
                        dataKey="name"
                        type="category"
                        width={100}
                        tick={{ fontSize: 11 }}
                      />
                      <Tooltip
                        formatter={(value) => [`${value}%`, "매칭 점수"]}
                      />
                      <Bar dataKey="score" radius={[0, 4, 4, 0]} barSize={24}>
                        {chartData.map((entry) => (
                          <Cell
                            key={entry.name}
                            fill={
                              typeof entry.jsd === "number"
                                ? jsdColor(entry.jsd)
                                : "#3b82f6"
                            }
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Hazard Results */}
              {hazardResult &&
                hazardResult.compound_results.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">
                      위해성 평가
                    </h4>
                    <div className="space-y-3">
                      {hazardResult.compound_results
                        .slice(0, 3)
                        .map((cr) => (
                          <div
                            key={cr.compound_name}
                            className="bg-gray-50 rounded-lg p-3"
                          >
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium text-gray-900">
                                {cr.compound_name}
                              </span>
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${riskBadgeClass(
                                  cr.overall_risk_level,
                                )}`}
                              >
                                위험도:{" "}
                                {Math.round(cr.overall_risk_score * 100)}%
                              </span>
                            </div>
                            {/* NFPA bar */}
                            {cr.nfpa_rating && (
                              <div className="flex gap-3 text-xs">
                                <span className="text-blue-600">
                                  건강: {cr.nfpa_rating.health}
                                </span>
                                <span className="text-red-600">
                                  인화성: {cr.nfpa_rating.flammability}
                                </span>
                                <span className="text-yellow-600">
                                  불안정: {cr.nfpa_rating.instability}
                                </span>
                              </div>
                            )}
                            {/* Primary hazards */}
                            {cr.primary_hazards.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {cr.primary_hazards
                                  .slice(0, 3)
                                  .map((h) => (
                                    <span
                                      key={h}
                                      className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-red-50 text-red-600"
                                    >
                                      {h}
                                    </span>
                                  ))}
                              </div>
                            )}
                          </div>
                        ))}
                    </div>
                  </div>
                )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
